<script lang="ts">
  import type { StepType } from '$lib/model/types';
  import { projectStore, selectStep } from '$lib/state/project.svelte';
  import { addChild } from '$lib/model/tree';
  import { allowedChildren, singleChild } from '$lib/model/validity';
  import {
    syncColorSeparateChildren,
    syncGradientSepChildren,
  } from '$lib/model/sync';
  import { triggerRecompute } from '$lib/compute/recompute.svelte';

  interface Props {
    parentStepId: string;
    onClose: () => void;
  }

  const { parentStepId, onClose }: Props = $props();

  const parent = $derived(projectStore.pipeline.steps[parentStepId]);
  const options = $derived(parent ? filtered(parent.type, parent) : []);

  function filtered(parentType: StepType, p: typeof parent): StepType[] {
    if (!p) return [];
    const allowed = allowedChildren(parentType);
    if (singleChild(parentType) && p.children.length >= 1) return [];
    return allowed;
  }

  function labelFor(t: StepType): string {
    switch (t) {
      case 'greyscale':
        return 'Greyscale';
      case 'color_separate':
        return 'Color separation';
      case 'gradient_sep':
        return 'Gradient separation';
      case 'halftone':
        return 'Halftone';
      default:
        return t;
    }
  }

  function add(type: StepType) {
    if (!parent) return;
    const newId = addChild(projectStore.pipeline, parentStepId, type, {
      inheritNameFromParent: type === 'halftone' && parent.type === 'channel',
    });
    if (type === 'color_separate') {
      syncColorSeparateChildren(projectStore.pipeline, newId);
    } else if (type === 'gradient_sep') {
      syncGradientSepChildren(projectStore.pipeline, newId);
    }
    // Halftones get default preview color from the parent channel's target
    // when added under a `channel`, so red-channel halftones default to red.
    if (type === 'halftone') {
      const created = projectStore.pipeline.steps[newId];
      if (created && created.preview && parent.type === 'channel') {
        const grandparentId = findParent(parentStepId);
        if (grandparentId) {
          const gp = projectStore.pipeline.steps[grandparentId];
          if (gp && gp.type === 'color_separate') {
            const idx = (parent.params as { channel_index: number }).channel_index;
            const channels = (gp.params as { channels: { target: string }[] }).channels;
            const target = channels[idx]?.target;
            if (target) created.preview.color = target;
          }
        }
      }
      // When added under a band, inherit the band's chosen ink color so the
      // halftone leaf starts in the right preview shade.
      if (created && created.preview && parent.type === 'band' && parent.preview) {
        created.preview.color = parent.preview.color;
      }
      projectStore.layer_order = [...projectStore.layer_order, newId];
    }
    selectStep(newId);
    triggerRecompute();
    onClose();
  }

  function findParent(childId: string): string | undefined {
    for (const [id, step] of Object.entries(projectStore.pipeline.steps)) {
      if (step.children.includes(childId)) return id;
    }
    return undefined;
  }
</script>

<div class="menu" role="menu">
  {#if options.length === 0}
    <p class="empty">No transforms available for this step.</p>
  {:else}
    {#each options as opt (opt)}
      <button onclick={() => add(opt)} role="menuitem">
        {labelFor(opt)}
      </button>
    {/each}
  {/if}
  <button class="close" onclick={onClose} aria-label="Close">cancel</button>
</div>

<style>
  .menu {
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: var(--surface-2);
    border: 1px solid var(--accent-dim);
    border-radius: var(--radius);
    padding: 4px;
    margin: 2px 0 6px 24px;
  }
  .menu button {
    text-align: left;
    background: transparent;
    border: 1px solid transparent;
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 3px;
  }
  .menu button:hover {
    background: var(--surface);
    border-color: var(--border);
  }
  .empty {
    margin: 0;
    color: var(--text-dim);
    font-size: 12px;
    padding: 4px 8px;
  }
  .close {
    color: var(--text-dim);
    font-style: italic;
  }
</style>
